// src/features/post/PostList.jsx
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPosts, deletePost, bulkDeletePosts } from './postSlice';
import Swal from 'sweetalert2';
import ConfirmationModal from '../../components/ConfirmationModal';
import Loader from '../../components/Loader';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactTable from '../../components/ReactTable';
import { paths } from '../../routes/helpers';

const PostList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.post
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);

  useEffect(() => {
    dispatch(fetchPosts({ page: currentPage, limit: currentLimit }));
  }, [dispatch, currentPage, currentLimit]);

  const handleDelete = (postId) => {
    setShowDeleteModal(true);
    setSelectedPost(postId);
  };

  const confirmDelete = () => {
    if (selectedPost) {
      dispatch(deletePost(selectedPost));
      toast.success('Post deleted successfully!');
      log.info('Post deleted:', selectedPost);
      setShowDeleteModal(false);
      setSelectedPost(null);
    }
  };

  const handleEdit = (postId) => {
    navigate(paths.private.post.edit(postId));
  };

  const handleCreatePost = () => {
    navigate(paths.private.post.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    dispatch(fetchPosts({ page: 1, limit: newLimit }));
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} post${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    dispatch(bulkDeletePosts(ids)).then(() => {
      Swal.fire({ title: 'Deleted!', text: `${ids.length} post${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (posts) => {
    return posts.map(p => ({
      _id: p._id,
      business: p.business ? p.business.title : '-',
      locations: p.locations.length > 0 
        ? p.locations.map(loc => loc.neighbourhood?.name).join(', ')
        : 'No locations',
      categories: p.categories.length > 0 
        ? p.categories.map(cat => cat.label).join(', ')
        : 'No categories',
      trends: p.trends.length > 0 
        ? p.trends.map(trend => trend.label).join(', ')
        : 'No trends',
      price: `$${p.minPrice} - $${p.maxPrice}`,
      rating: p.rating ? p.rating.toFixed(1) : '-',
      status: getStatusLabel(p.status),
      createdAt: new Date(p.createdAt).toLocaleDateString(),
    }));
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      0: 'Draft',
      1: 'Published', 
      2: 'Archived'
    };
    return statusMap[status] || 'Unknown';
  };

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  const headers = [
    { label: 'Business', key: 'business' },
    { label: 'Locations', key: 'locations' },
    { label: 'Categories', key: 'categories' },
    { label: 'Trends', key: 'trends' },
    { label: 'Price Range', key: 'price' },
    { label: 'Rating', key: 'rating' },
    { label: 'Status', key: 'status' },
    { label: 'Created', key: 'createdAt' }
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Posts</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreatePost}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Post
        </button>
        <div className="text-sm text-gray-600">Total: {total} posts</div>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[{ label: 'Delete Selected', color: 'red', onClick: handleBulkDelete }]}
      />

      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No posts found</p>
          <p className="text-gray-400 text-sm mt-2">Click the Create New button to add one</p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this post?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default PostList;